import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import Popover from '@/components/Popover';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import ReviewDetails from '@/features/faq/review-ratings/ReviewDetails';
import { TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { ReviewPayload } from '@/services/faq/faq.mutation';
import { RATING_FILTER, ReviewAndRating } from '@/services/faq/faq.types';

type ModalSize = 'sm' | 'md' | 'lg';
type ModalVariant = 'default' | 'destructive' | 'warning';

type OpenConfirmationProps = (data: {
  title: string;
  description: string;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction: () => Promise<void>;
  onCancel: () => void;
}) => void;

export function getColumns(
  t: (key: string) => string,
  locale: string,
  openConfirmation: OpenConfirmationProps,
  mutateAsyncReviewAndRatingsStatus: (payload: ReviewPayload) => Promise<unknown>,
): ColumnDef<ReviewAndRating>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => (
        <div className="font-medium items-start justify-start">{getSerialNumber(table, row.index)}</div>
      ),
      enableHiding: false,
      enableSorting: false,
    },
    {
      accessorKey: 'reviewId',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.reviewId')} />,
      cell: ({ row }) => {
        const resId = row.getValue<string>('reviewId');
        const truncateText = (text: string, maxLength = 12) =>
          text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
        return (
          <Can
            rule={{ allOf: [PERMS.reviewRatings.preview] }}
            fallback={<div className="text-caption font-medium text-primary">{truncateText(resId)}</div>}
          >
            <Popover
              trigger={
                <div className="text-caption font-medium text-link underline hover:cursor-pointer">
                  {truncateText(resId)}
                </div>
              }
              content={() => <ReviewDetails reviewId={resId} />}
            />
          </Can>
        );
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.reviewId',
      },
    },
    {
      accessorKey: 'nickName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.nickname')} />,
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('nickName')}</div>,
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.nickname',
      },
    },
    {
      accessorKey: 'ratingScale',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.ratings')} />,
      cell: ({ row }) => {
        const rating = row.getValue<number>('ratingScale');
        return (
          <div className="flex gap-0.5 text-status-warning">
            {Array.from({ length: 5 }, (_, i) => (
              <span key={i}>{i < rating ? '★' : '☆'}</span>
            ))}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.ratings',
        label: t('faq.reviewRating.ratingFilter.title'),
        options: [
          {
            label: '★☆☆☆☆',
            value: RATING_FILTER.One,
          },
          {
            label: '★★☆☆☆',
            value: RATING_FILTER.Two,
          },
          {
            label: '★★★☆☆',
            value: RATING_FILTER.Three,
          },
          {
            label: '★★★★☆',
            value: RATING_FILTER.Four,
          },
          {
            label: '★★★★★',
            value: RATING_FILTER.Five,
          },
        ],
      },
    },
    {
      accessorKey: 'japaneseRemark',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.reviews')} />,
      cell: ({ row }) => {
        const reviews: string = row.getValue('japaneseRemark') || '';
        const jpReviews: string = row.getValue('japaneseRemark') || '';
        const reviewList = reviews.split(',').map((item) => item.trim());
        const jpReviewList = jpReviews.split(',').map((item) => item.trim());
        return (
          <ul className="text-caption font-medium text-primary list-disc list-inside space-y-0.5 min-w-[218px]">
            {locale === 'en'
              ? reviewList.map((review, i) => <li key={i}>{review}</li>)
              : jpReviewList.map((review, i) => <li key={i}>{review}</li>)}
          </ul>
        );
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.reviews',
      },
    },
    {
      accessorKey: 'message',
      header: ({ column }) => (
        <DataTableColumnHeader className="w-[218px]" column={column} title={t('faq.reviewRating.table.message')} />
      ),
      cell: ({ row }) => {
        const value = row.getValue<string>('message');
        return <div className="text-caption font-medium text-primary break-words max-w-[218px]">{value || '-'}</div>;
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.message',
      },
    },
    {
      accessorKey: 'storeNameEng',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.storeInfo')} />,
      cell: ({ row }) => {
        return (
          <div className="flex items-center space-x-2 min-w-[162px]">
            <div className="w-[32px] h-[32px] rounded-full overflow-hidden">
              <img
                src={row.original.clubImageResponse.path}
                alt={row.original.clubImageResponse.alt}
                className="object-cover"
              />
            </div>
            <div className="truncate">
              <div className="text-caption font-medium text-link underline">{row.original.storeNameEng}</div>
              <div className="text-caption font-medium text-foreground-subtle">{row.original.storeNameJp}</div>
            </div>
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.storeInfo',
      },
    },
    {
      accessorKey: 'visitingDateTime',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.reservationDate')} />
      ),
      cell: ({ row }) => {
        const visitingDateTime = row.original.visitingDateTime;
        const endingDateTime = row.original.endingDateTime;
        const date = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[0];
        const visitingTime = formatDate(visitingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];
        const endingTime = formatDate(endingDateTime, 'YYYY/MM/DD HH:mm').split(' ')[1];
        return (
          <div className="text-caption font-medium text-primary min-w-[200px]">
            {`${date ? date : '-'} ${visitingDateTime ? visitingTime : '-'} ~ ${endingDateTime ? endingTime : '-'}`}
          </div>
        );
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.reservationDate',
      },
    },
    {
      accessorKey: 'createdDate',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('faq.reviewRating.table.postedDate')} />,
      cell: ({ row }) => {
        const date = new Date(row.getValue<Date>('createdDate'));
        return <div className="text-caption font-medium text-primary">{formatDate(date)}</div>;
      },
      meta: {
        hideColumnLabelKey: 'faq.reviewRating.table.postedDate',
      },
    },
    {
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      cell: ({ row }) => (
        <Can rule={{ allOf: [PERMS.reviewRatings.delete] }}>
          <TrashIcon
            className="text-destructive-text size-4 hover:cursor-pointer"
            onClick={() => {
              openConfirmation({
                title: t('faq.reviewRating.deleteModel.confirmation'),
                description: t('faq.reviewRating.deleteModel.title'),
                cancelLabel: t('common.page.modal.no'),
                actionLabel: t('common.page.modal.submit'),
                size: 'lg',
                onAction: async () => {
                  await mutateAsyncReviewAndRatingsStatus({
                    reviewId: row.original.reviewId,
                  });
                },
                onCancel: () => {},
                variant: 'destructive',
              });
            }}
          />
        </Can>
      ),
      enableHiding: false,
      enableSorting: false,
    },
  ];
}
